import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, onValue, off, update } from "firebase/database";

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [borrowedBooks, setBorrowedBooks] = useState([]);
  const [returnedBooks, setReturnedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("books");
  const [searchWord, setSearchWord] = useState("");
  const [dictionaryResult, setDictionaryResult] = useState(null);
  const [isSearchingDict, setIsSearchingDict] = useState(false);
  const [elibraryLinks, setElibraryLinks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderView, setShowFolderView] = useState(false);
  const [settings, setSettings] = useState({
    finePerDay: 10,
    issueDays: 7
  });

  const teacherId = localStorage.getItem("teacherId");
  const teacherName = localStorage.getItem("userName") || "Teacher";

  // Bottom navigation items with unique IDs
  const bottomNavItems = [
   ];

  useEffect(() => {
    const settingsRef = ref(db, "librarySettings");
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) setSettings({ finePerDay: data.finePerDay || 10, issueDays: data.issueDays || 7 });
    });
    return () => unsubscribe();
  }, []);

  // Fetch folders from admin E-Library
  useEffect(() => {
    const foldersRef = ref(db, "elibrary_folders");
    const unsubscribe = onValue(foldersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const folderList = Object.entries(data).map(([id, folder]) => ({ id, ...folder }));
        setFolders(folderList);
      } else {
        setFolders([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch resources when a folder is selected
  useEffect(() => {
    if (!selectedFolder) {
      setElibraryLinks([]);
      return;
    }
    
    const resourcesRef = ref(db, `elibrary_resources/${selectedFolder.id}`);
    const unsubscribe = onValue(resourcesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const resourceList = Object.entries(data).map(([id, resource]) => ({ id, ...resource }));
        setElibraryLinks(resourceList);
      } else {
        setElibraryLinks([]);
      }
    });
    return () => unsubscribe();
  }, [selectedFolder]);

  useEffect(() => {
    if (!teacherId) return;
    const transactionsRef = ref(db, "transactions");
    const unsubscribe = onValue(transactionsRef, (snapshot) => {
      const data = snapshot.val();
      const borrowed = [], returned = [];
      if (data) {
        Object.entries(data).forEach(([id, transaction]) => {
          if (transaction.teacherId === teacherId || transaction.teacherId === teacherId.toString()) {
            const today = new Date();
            const dueDate = transaction.dueDate ? new Date(transaction.dueDate) : null;
            const isOverdue = dueDate && dueDate < today && transaction.status === "issued";
            let fine = 0, daysLate = 0;
            if (isOverdue && transaction.status === "issued") {
              daysLate = Math.ceil((today - dueDate) / (1000 * 60 * 60 * 24));
              fine = daysLate * settings.finePerDay;
            }
            const bookData = { id, ...transaction, isOverdue, fine, daysLate, dueDateObj: dueDate };
            if (transaction.status === "issued") borrowed.push(bookData);
            else if (transaction.status === "returned") returned.push(bookData);
          }
        });
      }
      setBorrowedBooks(borrowed);
      setReturnedBooks(returned);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [teacherId, settings.finePerDay]);

  // Convert Google Drive link to direct download/view link
  const convertDriveLink = (url) => {
    // Google Drive link patterns
    const drivePatterns = [
      /drive\.google\.com\/file\/d\/([^\/]+)\/view/,
      /drive\.google\.com\/open\?id=([^&]+)/,
      /drive\.google\.com\/uc\?id=([^&]+)/,
      /drive\.google\.com\/file\/d\/([^\/]+)/
    ];
    
    for (const pattern of drivePatterns) {
      const match = url.match(pattern);
      if (match) {
        const fileId = match[1];
        return `https://drive.google.com/uc?export=view&id=${fileId}`;
      }
    }
    return url;
  };

  // Check if URL is a PDF
  const isPdfUrl = (url) => {
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.pdf') || 
           lowerUrl.includes('.pdf?') ||
           lowerUrl.includes('drive.google.com') ||
           lowerUrl.includes('docs.google.com/viewer');
  };

  // Open content properly
  const openContent = (url, title) => {
    let finalUrl = convertDriveLink(url);
    
    // For PDFs or Google Drive files, open in new tab (browser handles PDF viewing)
    // For regular websites, also open in new tab
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const selectFolder = (folder) => {
    setSelectedFolder(folder);
    setShowFolderView(true);
  };

  const goBackToFolders = () => {
    setSelectedFolder(null);
    setShowFolderView(false);
    setElibraryLinks([]);
  };

  const searchDictionary = async () => {
    if (!searchWord.trim()) {
      alert("Please enter a word to search");
      return;
    }

    setIsSearchingDict(true);
    setDictionaryResult(null);

    try {
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(searchWord)}`);
      
      if (response.ok) {
        const data = await response.json();
        const meaning = data[0];
        
        setDictionaryResult({
          word: meaning.word,
          phonetic: meaning.phonetic || "",
          meanings: meaning.meanings.map(m => ({
            partOfSpeech: m.partOfSpeech,
            definitions: m.definitions.slice(0, 2).map(d => d.definition)
          })),
          hindiMeaning: getHindiMeaning(searchWord)
        });
      } else {
        setDictionaryResult({
          word: searchWord,
          error: "Word not found",
          hindiMeaning: getHindiMeaning(searchWord)
        });
      }
    } catch (error) {
      setDictionaryResult({
        word: searchWord,
        error: "Could not fetch meaning",
        hindiMeaning: "हिंदी अर्थ उपलब्ध नहीं है"
      });
    } finally {
      setIsSearchingDict(false);
    }
  };

  const getHindiMeaning = (word) => {
    const hindiWords = {
      "apple": "सेब", "book": "पुस्तक", "car": "कार", "house": "घर",
      "water": "पानी", "sun": "सूर्य", "moon": "चंद्रमा", "star": "तारा",
      "flower": "फूल", "tree": "पेड़", "bird": "पक्षी", "animal": "जानवर",
      "happy": "खुश", "sad": "उदास", "big": "बड़ा", "small": "छोटा"
    };
    return hindiWords[word.toLowerCase()] || "हिंदी अर्थ उपलब्ध नहीं है";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  const totalBorrowed = borrowedBooks.length;
  const overdueCount = borrowedBooks.filter(b => b.isOverdue).length;
  const totalFine = borrowedBooks.reduce((sum, b) => sum + b.fine, 0);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👩‍🏫 Teacher Dashboard</h1>
          <p style={styles.subtitle}>Welcome back, {teacherName}!</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>{teacherName.charAt(0).toUpperCase()}</div>
          </div>
        </div>
      </div>

      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { setShowMobileMenu(false); }}><span>👤</span> My Profile</div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}><span>🚪</span> Logout</div>
        </div>
      )}

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📚</span>
          <div>
            <h3 style={styles.statValue}>{totalBorrowed}</h3>
            <p style={styles.statLabel}>Books Borrowed</p>
          </div>
        </div>
        <div style={{...styles.statCard, ...(overdueCount > 0 ? styles.warningCard : {})}}>
          <span style={styles.statIcon}>⚠️</span>
          <div>
            <h3 style={{...styles.statValue, color: overdueCount > 0 ? "#e53e3e" : "#2d3748"}}>{overdueCount}</h3>
            <p style={styles.statLabel}>Overdue Books</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>💰</span>
          <div>
            <h3 style={styles.statValue}>₹{totalFine}</h3>
            <p style={styles.statLabel}>Total Fine</p>
          </div>
        </div>
      </div>

      <div style={styles.tabContainer}>
        <button style={activeTab === "books" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("books")}>📚 My Books</button>
        <button style={activeTab === "elibrary" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("elibrary")}>📖 E-Library</button>
        <button style={activeTab === "dictionary" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("dictionary")}>📝 Dictionary</button>
      </div>

      {/* Books Tab */}
      {activeTab === "books" && (
        <>
          <div style={styles.sectionCard}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>📖 Currently Borrowed Books</h2>
              <span style={styles.sectionCount}>{borrowedBooks.length} books</span>
            </div>
            {borrowedBooks.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyIcon}>📚</span>
                <p>No books borrowed currently</p>
              </div>
            ) : (
              <div style={styles.booksList}>
                {borrowedBooks.map((book) => (
                  <div key={book.id} style={{...styles.bookCard, ...(book.isOverdue ? styles.overdueCard : {})}}>
                    <div style={styles.bookHeader}>
                      <span style={styles.bookIcon}>📖</span>
                      <span style={book.isOverdue ? styles.overdueBadge : styles.issuedBadge}>
                        {book.isOverdue ? "Overdue!" : "Issued"}
                      </span>
                    </div>
                    <h3 style={styles.bookTitle}>{book.bookTitle}</h3>
                    <p style={styles.bookAuthor}>by {book.bookAuthor || "Unknown Author"}</p>
                    <div style={styles.bookDetails}>
                      <div style={styles.detailRow}>
                        <span>📅 Issued:</span>
                        <strong>{formatDate(book.issueDate)}</strong>
                      </div>
                      <div style={styles.detailRow}>
                        <span>📅 Due Date:</span>
                        <strong style={{color: book.isOverdue ? "#e53e3e" : "#2d3748"}}>
                          {formatDate(book.dueDate)}
                        </strong>
                      </div>
                      {book.isOverdue && (
                        <>
                          <div style={styles.detailRow}>
                            <span>⏰ Days Late:</span>
                            <strong style={{color: "#e53e3e"}}>{book.daysLate} days</strong>
                          </div>
                          <div style={styles.detailRow}>
                            <span>💰 Fine:</span>
                            <strong style={{color: "#e53e3e"}}>₹{book.fine}</strong>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {returnedBooks.length > 0 && (
            <div style={styles.sectionCard}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>✅ Previously Returned Books</h2>
                <span style={styles.sectionCount}>{returnedBooks.length} books</span>
              </div>
              <div style={styles.returnedList}>
                {returnedBooks.slice(0, 10).map((book) => (
                  <div key={book.id} style={styles.returnedItem}>
                    <div>
                      <strong>{book.bookTitle}</strong>
                      <div style={styles.returnedMeta}>
                        Returned: {formatDate(book.returnDate)}
                        {book.fine > 0 && <span style={styles.fineText}> • Fine: ₹{book.fine}</span>}
                      </div>
                    </div>
                    <span style={styles.returnedIcon}>✅</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* E-Library Tab - Folder View */}
      {activeTab === "elibrary" && (
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>📚 E-Library Resources</h2>
            <p style={styles.sectionSubtitle}>Access digital resources organized in folders</p>
          </div>
          
          {!showFolderView ? (
            // Show Folders
            <>
              <div style={styles.foldersGrid}>
                {folders.map((folder) => (
                  <div key={folder.id} style={styles.folderCard} onClick={() => selectFolder(folder)}>
                    <div style={styles.folderIconLarge}>{folder.icon || "📁"}</div>
                    <h3 style={styles.folderNameLarge}>{folder.name}</h3>
                    <p style={styles.folderDescLarge}>{folder.description || "No description"}</p>
                    <div style={styles.folderStatsLarge}>
                      <span>📄 {folder.resourceCount || 0} resources</span>
                    </div>
                  </div>
                ))}
                {folders.length === 0 && (
                  <div style={styles.emptyFolders}>
                    <span>📁</span>
                    <p>No folders available. Admin will add resources soon.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Show Resources in Selected Folder
            <>
              <div style={styles.backButtonContainer}>
                <button onClick={goBackToFolders} style={styles.backButton}>
                  ← Back to Folders
                </button>
                <h3 style={styles.currentFolderTitle}>{selectedFolder?.name}</h3>
              </div>
              
              <div style={styles.resourcesGrid}>
                {elibraryLinks.map((resource) => (
                  <div key={resource.id} style={styles.resourceCard} onClick={() => openContent(resource.url, resource.title)}>
                    <div style={styles.resourceIconLarge}>📄</div>
                    <h3 style={styles.resourceTitleLarge}>{resource.title}</h3>
                    <p style={styles.resourceDescLarge}>{resource.description || "Click to open"}</p>
                    <div style={styles.resourceType}>
                      {resource.url.includes('drive.google.com') ? '📁 Google Drive' : 
                       resource.url.toLowerCase().endsWith('.pdf') ? '📄 PDF Document' : '🔗 Web Link'}
                    </div>
                  </div>
                ))}
                {elibraryLinks.length === 0 && (
                  <div style={styles.emptyResources}>
                    <span>📭</span>
                    <p>No resources in this folder yet.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Dictionary Tab */}
      {activeTab === "dictionary" && (
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>📝 English Dictionary</h2>
            <p style={styles.sectionSubtitle}>Search English words with Hindi meanings</p>
          </div>
          <div style={styles.dictionarySearch}>
            <input
              type="text"
              placeholder="Enter English word to search..."
              value={searchWord}
              onChange={(e) => setSearchWord(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && searchDictionary()}
              style={styles.dictionaryInput}
            />
            <button onClick={searchDictionary} style={styles.dictionaryBtn} disabled={isSearchingDict}>
              {isSearchingDict ? "Searching..." : "🔍 Search"}
            </button>
          </div>
          {dictionaryResult && (
            <div style={styles.dictionaryResult}>
              <div style={styles.resultWord}>
                <h3>{dictionaryResult.word}</h3>
                {dictionaryResult.phonetic && <span>{dictionaryResult.phonetic}</span>}
              </div>
              {dictionaryResult.error ? (
                <p style={styles.errorText}>{dictionaryResult.error}</p>
              ) : (
                <>
                  <div style={styles.meaningSection}>
                    <h4>English Meaning:</h4>
                    {dictionaryResult.meanings?.map((meaning, idx) => (
                      <div key={idx} style={styles.meaningItem}>
                        <strong>{meaning.partOfSpeech}:</strong>
                        <ul>
                          {meaning.definitions.map((def, didx) => (
                            <li key={didx}>{def}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <div style={styles.hindiSection}>
                    <h4>हिंदी अर्थ (Hindi Meaning):</h4>
                    <p>{dictionaryResult.hindiMeaning}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div
            key={item.id}
            style={{
              ...styles.bottomNavItem,
              ...(location.pathname === item.path && styles.bottomNavItemActive)
            }}
            onClick={() => handleNavigation(item.path)}
          >
            <div style={styles.bottomNavIcon}>
              {location.pathname === item.path ? item.activeIcon : item.icon}
            </div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "clamp(16px, 4vw, 32px)",
    paddingBottom: "80px",
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    backgroundColor: "#f7fafc",
    minHeight: "100vh",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f7fafc",
  },
  loader: {
    width: "50px",
    height: "50px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },
  title: {
    fontSize: "clamp(24px, 5vw, 32px)",
    fontWeight: "700",
    margin: "0 0 8px 0",
    color: "#2d3748",
  },
  subtitle: {
    fontSize: "14px",
    color: "#718096",
    margin: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  adminProfile: { cursor: "pointer" },
  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },
  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },
  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  statCard: {
    backgroundColor: "#fff",
    padding: "16px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },
  warningCard: {
    backgroundColor: "rgba(229, 62, 62, 0.05)",
    borderColor: "#e53e3e",
  },
  statIcon: { fontSize: "28px" },
  statValue: { fontSize: "22px", fontWeight: "700", margin: 0, color: "#2d3748" },
  statLabel: { fontSize: "11px", color: "#718096", margin: 0 },
  tabContainer: {
    display: "flex",
    gap: "8px",
    marginBottom: "20px",
    flexWrap: "wrap",
    backgroundColor: "#fff",
    padding: "6px",
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  tab: {
    flex: 1,
    padding: "10px 12px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    color: "#64748b",
  },
  activeTab: {
    flex: 1,
    padding: "10px 12px",
    backgroundColor: "#667eea",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    color: "#fff",
  },
  sectionCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },
  sectionHeader: { marginBottom: "16px" },
  sectionTitle: { fontSize: "18px", fontWeight: "600", margin: "0 0 4px 0", color: "#2d3748" },
  sectionSubtitle: { fontSize: "12px", color: "#718096", margin: 0 },
  sectionCount: { fontSize: "12px", color: "#718096", backgroundColor: "#edf2f7", padding: "4px 10px", borderRadius: "20px" },
  emptyState: { textAlign: "center", padding: "40px", color: "#a0aec0" },
  emptyIcon: { fontSize: "48px", display: "block", marginBottom: "16px" },
  booksList: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" },
  bookCard: { backgroundColor: "#f7fafc", borderRadius: "12px", padding: "16px", border: "1px solid #e2e8f0", transition: "transform 0.2s ease" },
  overdueCard: { backgroundColor: "rgba(229, 62, 62, 0.05)", borderColor: "#e53e3e" },
  bookHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" },
  bookIcon: { fontSize: "28px" },
  issuedBadge: { fontSize: "11px", padding: "4px 10px", borderRadius: "20px", backgroundColor: "#c6f6d5", color: "#22543d", fontWeight: "600" },
  overdueBadge: { fontSize: "11px", padding: "4px 10px", borderRadius: "20px", backgroundColor: "#fed7d7", color: "#742a2a", fontWeight: "600" },
  bookTitle: { fontSize: "16px", fontWeight: "600", margin: "0 0 4px 0", color: "#2d3748" },
  bookAuthor: { fontSize: "13px", color: "#718096", margin: "0 0 12px 0" },
  bookDetails: { marginBottom: "16px" },
  detailRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px", color: "#4a5568" },
  returnedList: { display: "flex", flexDirection: "column", gap: "12px" },
  returnedItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", backgroundColor: "#f7fafc", borderRadius: "8px" },
  returnedMeta: { fontSize: "11px", color: "#718096", marginTop: "4px" },
  fineText: { color: "#e53e3e" },
  returnedIcon: { fontSize: "20px" },
  // E-Library Folder Styles
  foldersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "20px",
  },
  folderCard: {
    backgroundColor: "#f7fafc",
    borderRadius: "12px",
    padding: "20px",
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.3s ease",
    border: "1px solid #e2e8f0",
  },
  folderIconLarge: { fontSize: "48px", marginBottom: "12px" },
  folderNameLarge: { fontSize: "16px", fontWeight: "600", margin: "0 0 8px 0", color: "#2d3748" },
  folderDescLarge: { fontSize: "12px", color: "#718096", margin: "0 0 12px 0" },
  folderStatsLarge: { fontSize: "11px", color: "#a0aec0" },
  emptyFolders: { textAlign: "center", padding: "60px", color: "#a0aec0" },
  backButtonContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  backButton: {
    padding: "8px 16px",
    backgroundColor: "#e2e8f0",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
  },
  currentFolderTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
    color: "#2d3748",
  },
  resourcesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
  },
  resourceCard: {
    backgroundColor: "#f7fafc",
    borderRadius: "12px",
    padding: "16px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid #e2e8f0",
  },
  resourceIconLarge: { fontSize: "40px", marginBottom: "8px" },
  resourceTitleLarge: { fontSize: "16px", fontWeight: "600", margin: "0 0 8px 0", color: "#2d3748" },
  resourceDescLarge: { fontSize: "12px", color: "#718096", margin: "0 0 12px 0" },
  resourceType: { fontSize: "11px", color: "#667eea", marginTop: "8px" },
  emptyResources: { textAlign: "center", padding: "60px", color: "#a0aec0" },
  dictionarySearch: { display: "flex", gap: "12px", marginBottom: "20px" },
  dictionaryInput: { flex: 1, padding: "12px", borderRadius: "10px", border: "2px solid #e2e8f0", fontSize: "14px" },
  dictionaryBtn: { padding: "12px 24px", backgroundColor: "#667eea", color: "#fff", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  dictionaryResult: { marginTop: "20px", padding: "16px", backgroundColor: "#f7fafc", borderRadius: "12px" },
  resultWord: { marginBottom: "16px", paddingBottom: "12px", borderBottom: "2px solid #e2e8f0" },
  meaningSection: { marginBottom: "16px" },
  meaningItem: { marginBottom: "12px" },
  hindiSection: { marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" },
  errorText: { color: "#e53e3e", textAlign: "center", padding: "20px" },
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 16px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },
  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },
  bottomNavItemActive: { color: "#667eea" },
  bottomNavIcon: { fontSize: "22px" },
  bottomNavLabel: { fontSize: "11px", fontWeight: "500" },
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .book-card:hover, .folder-card:hover, .resource-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
  @media (max-width: 768px) { 
    .stats-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; } 
    .stat-card { padding: 10px; } 
    .stat-icon { font-size: 22px; } 
    .stat-value { font-size: 18px; } 
    .tab-container { flex-direction: column; } 
    .books-list, .folders-grid, .resources-grid { grid-template-columns: 1fr; } 
    .dictionary-search { flex-direction: column; } 
  }
`;
document.head.appendChild(styleSheet);